import { useEffect, useMemo, useState } from "react";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { useTranslation } from "react-i18next";
import { useDesignToggle } from "~/features/orders/common/components/NewDesignToggle";

enum DROPDOWN_TYPE {
  ORDER_STATUS,
  ORDER_LINE_STAUTS,
  QUOTE_STATUS,
  QUOTE_LINE_STATUS,
}

export const ALL_CODE = "all";

const VARIANT_MAP: Dict<{ document: DROPDOWN_TYPE; lineItem: DROPDOWN_TYPE }> = {
  [SearchBoxVariant.ORDER]: {
    document: DROPDOWN_TYPE.ORDER_STATUS,
    lineItem: DROPDOWN_TYPE.ORDER_LINE_STAUTS,
  },
  [SearchBoxVariant.QUOTE]: {
    document: DROPDOWN_TYPE.QUOTE_STATUS,
    lineItem: DROPDOWN_TYPE.QUOTE_LINE_STATUS,
  },
};

export const useDropdownDataProvider = (variant: SearchBoxVariant) => {
  const { t } = useTranslation();
  const [orderStatusOptions, setOrderStatusOptions] = useState<DropdownOption[]>([]);
  const [lineItemStatusOptions, setLineItemStatusOptions] = useState<DropdownOption[]>([]);
  const [supportedSearchTypes, setSupportedSearchTypes] = useState<string[] | undefined>();
  const [loading, setLoading] = useState(true);
  const { isNewDesignEnabled } = useDesignToggle();

  useEffect(() => {
    let active = true;
    const loadOptions = async () => {
      setLoading(true);
      const [status, lineItemStatus] = await Promise.all([
        getOrderQuotesDropdownsOptions(VARIANT_MAP[variant].document),
        getOrderQuotesDropdownsOptions(VARIANT_MAP[variant].lineItem),
      ]);
      if (!active) {
        return;
      }
      setOrderStatusOptions(status.options);
      setSupportedSearchTypes(status.supportedSearchTypes);
      setLineItemStatusOptions(lineItemStatus.options);
      setLoading(false);
    };

    void loadOptions();
    return () => {
      active = false;
    };
  }, [variant]);

  const transform = (item: { label: string; id: string }) => ({
    label: t(item.label),
    value: item.id,
  });

  const orderStatuses = useMemo(() => {
    const source = isNewDesignEnabled ? filterOrderStatuses(orderStatusOptions, variant) : orderStatusOptions;
    return source.map(transform);
  }, [orderStatusOptions, isNewDesignEnabled, variant]);

  const lineItemStatuses = useMemo(
    () => lineItemStatusOptions.map(transform),
    [lineItemStatusOptions, isNewDesignEnabled],
  );

  return {
    loading,
    orderStatuses,
    lineItemStatuses,
    supportedSearchTypes,
  };
};

const getOrderQuotesDropdownsOptions = (type: DROPDOWN_TYPE): Promise<DropdownData> => {
  let url: string;
  let request: (url: string, init?: RequestInit) => Promise<Response> = fetch;

  switch (type) {
    case DROPDOWN_TYPE.ORDER_STATUS:
      url = occUrl("/orders/options");
      request = occFetch;
      break;
    case DROPDOWN_TYPE.ORDER_LINE_STAUTS:
      url = occUrl("/orders/line-options");
      request = occFetch;
      break;
    case DROPDOWN_TYPE.QUOTE_STATUS:
      url = occUrl("/quotes/options");
      request = occFetch;
      break;
    case DROPDOWN_TYPE.QUOTE_LINE_STATUS:
      url = occUrl("/quotes/line-options");
      request = occFetch;
      break;
    default:
      return Promise.reject(new Error("Please provide a DROPDOWN TYPE parameter"));
  }

  return request(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    method: "GET",
  })
    .then((data) => data.json())
    .then((data: SelectionOptionListResponseDTO) => ({
      options: (data.options ?? []).map((item) => ({ id: item.id ?? "", label: item.label }) as DropdownOption),
      supportedSearchTypes: data.supportedSearchTypes,
    }));
};

interface DropdownData {
  options: DropdownOption[];
  supportedSearchTypes?: string[];
}

interface DropdownOption {
  id: string;
  label: string;
}

interface SelectionOptionListResponseDTO {
  options?: DropdownOption[];
  supportedSearchTypes?: string[];
}

const ORDER_STATUS_WHITELIST = new Set<string>([
  "order.status.definition.all",
  "order.status.definition.invoiced",
  "order.status.definition.delivered",
]);

const QUOTE_STATUS_WHITELIST = new Set<string>([
  "quote.status.definition.all",
  "quote.status.definition.active",
  "quote.status.definition.expired",
  "quote.status.definition.expiringSoon",
]);

export const filterOrderStatuses = (options: DropdownOption[], variant: SearchBoxVariant) => {
  if (variant !== SearchBoxVariant.ORDER) {
    return options.filter((opt) => QUOTE_STATUS_WHITELIST.has(opt.label));
  }
  return options.filter((opt) => ORDER_STATUS_WHITELIST.has(opt.label));
};
